import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapTemplate } from '@templates';
import {
  AssetTypeConfig,
  emptyServiceLocationDetails,
  ErrorMessages,
  BasicAssetType,
} from '@types';
import {
  addressResponseType,
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
  errorFormatter,
  issueLocationValidation,
  stringFieldSchema,
} from '@utils';
import { ZodError } from 'zod';
import { LatLngTuple } from 'leaflet';
import { SelectedLocationTextContent } from './SelectedLocationTextContent';
import { useSignsBollardsStore } from '../../../../hooks';
import { RoutesList } from '../../../../routes/Routes';

export const IssueLocation: FunctionComponent = () => {
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const {
    update,
    data: {
      serviceLocationDetails,
      assetDetails,
      issueType,
      isPhotoLocationStored,
    },
  } = useSignsBollardsStore();

  const errorPath = `issue-Location`;
  const pageTitle = isPhotoLocationStored
    ? 'Check if the location is correct'
    : 'Where is the issue located?';

  const subText = isPhotoLocationStored
    ? 'We used your photo to find where the issue is located. You can change where the issue is located using the map.'
    : 'Select where the issue is on the map by searching or zooming in. Each location is marked by a blue circle.';

  const { latitude, longitude } = serviceLocationDetails;

  const defaultLocation =
    latitude && longitude ? ([latitude, longitude] as LatLngTuple) : undefined;

  const handleOnPreviousClick = () => {
    navigate(RoutesList.UPLOAD_PHOTO);
  };

  const handleOnNextClick = () => {
    setErrorMessages([]);

    try {
      issueLocationValidation.parse({
        locationIssue: serviceLocationDetails.UPRN ?? '',
      });
      stringFieldSchema('assetID', 'Select a blue circle on the map').parse({
        assetID: assetDetails?.central_asset_id || '',
      });
      navigate(RoutesList.LOCATION_ADDITIONAL_INFORMATION);
    } catch (error) {
      const { errorMessages } = errorFormatter(error as ZodError);
      setErrorMessages([
        { path: errorPath, message: errorMessages[0].message },
      ]);
    }
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handleOnPreviousClick,
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  const handleAddressChange = async (address: addressResponseType) => {
    setErrorMessages([]);
    update('assetDetails', null);
    if (Object.keys(address).length === 0) {
      // Clears address and asset details when user clears address from search bar
      update('serviceLocationDetails', emptyServiceLocationDetails);
      return;
    } else {
      update(
        'serviceLocationDetails',
        convertAddressToServiceLocation(address),
      );

      const featureCode = (address.asset as BasicAssetType)?.feature_type_code;
      if (
        featureCode === 'HWSN' ||
        featureCode === 'HWSG' ||
        featureCode === 'HWBD'
      ) {
        update('assetDetails', address.asset as BasicAssetType);
      }
    }
  };

  const bollardsAsset: AssetTypeConfig = {
    asset: 'HighwaysBollards',
    icon: {
      shape: 'circle',
      size: 56,
    },
    onAdd(geojson, marker) {
      const markerElement = marker.getElement();
      const description = `${geojson.properties.site_name}, Bollard:${geojson.properties.central_asset_id}`;
      if (markerElement) {
        markerElement.setAttribute('alt', description);
      }
    },
  };

  const nameplatesAsset: AssetTypeConfig = {
    asset: 'HighwaysNameplates',
    icon: {
      shape: 'circle',
      size: 56,
    },
    onAdd(geojson, marker) {
      const markerElement = marker.getElement();
      const description = `${geojson.properties.site_name}, Street nameplate:${geojson.properties.central_asset_id}`;
      if (markerElement) {
        markerElement.setAttribute('alt', description);
      }
    },
  };

  const signsAsset: AssetTypeConfig = {
    asset: 'HighwaysSigns',
    icon: {
      shape: 'circle',
      size: 56,
    },
    onAdd(geojson, marker) {
      const markerElement = marker.getElement();
      const description = `${geojson.properties.site_name}, Sign:${geojson.properties.central_asset_id}`;
      if (markerElement) {
        markerElement.setAttribute('alt', description);
      }
    },
  };

  let assetToDisplay: AssetTypeConfig;

  if (issueType === 'Bollard') {
    assetToDisplay = bollardsAsset;
  } else if (issueType === 'StreetNameplate') {
    assetToDisplay = nameplatesAsset;
  } else {
    assetToDisplay = signsAsset;
  }

  const minZoomToDisplayAssets = {
    targetAssetType: assetToDisplay.asset,
    minZoom: 18,
  };

  return (
    <MapTemplate
      id={errorPath}
      labelText={pageTitle}
      assetType={[assetToDisplay]}
      displayMapAssets={true}
      subText={subText}
      errorPath={errorPath}
      errorMessages={errorMessages}
      defaultLocation={defaultLocation}
      onChangeAddress={handleAddressChange}
      selectedAddress={convertServiceLocationToAddress(serviceLocationDetails)}
      customAddressContent={<SelectedLocationTextContent />}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      minZoomToDisplayAssets={minZoomToDisplayAssets}
    />
  );
};
